import {Component, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {BaseInventoryOverride} from '../../models/base-inventory-override.model';
import {InventoryOverrideService} from '../../services/inventory-override.service';
import {Bucket, ClassContraintRequest} from '../../../../inventory/inventory.model';
import {JourneyShoulderTrainRequest} from '../../../charts/shared/models/shoulder-chart.model';
import {Observable} from 'rxjs';
import {DialogCloseResult, DialogRef} from '@progress/kendo-angular-dialog';
import {map} from 'rxjs/operators';
import {AlertDialogService, OverrideSendChoice} from '../../services/alert-dialog.service';
import {DateUtils} from '../../../date-utils';
import {ReminderService} from '../../../services/reminder.service';
import {ReminderRequest} from '../../../models/reminder.model';
import {BaseBucketOverrideGrid} from '../../models/base-bucket-override-grid.model';
import {NotificationPopupService} from '@shared/notification-popup/services/notification-popup.service';
import {NotificationService} from '../../../components/notification/notification.service';

@Component({
    selector: 'app-single-inventory-override',
    providers: [ BaseBucketOverrideGrid ],
    templateUrl: './single-inventory-override.component.html',
    styleUrls: ['./single-inventory-override.component.scss']
})
export class SingleInventoryOverrideComponent extends BaseInventoryOverride implements OnInit {
    @ViewChild('alertContainer', { read: ViewContainerRef }) alertContainer: ViewContainerRef;
    public shoulderChartRequest: JourneyShoulderTrainRequest;
    public selectedProfile = '';
    /** Set when a requested RARS send did not go through, so the modal can say why. */
    public sendMessage = '';
    /**
     * Where the success toast is shown. Set by OverrideDialogService to the page's
     * container, because this modal closes on a successful send and anything appended
     * inside it would vanish with it.
     */
    public notifyContainer: ViewContainerRef;
    private initBuckets: any;

    constructor(public dialog: DialogRef, private overrideSvc: InventoryOverrideService,
                private alertDialogSvc: AlertDialogService, private reminderSvc: ReminderService,
                private baseBucketOverrideGrid: BaseBucketOverrideGrid,
                private popupSvc: NotificationPopupService,
                private notificationSvc: NotificationService) {
        super(dialog);
    }

    ngOnInit(): void {
        this.getOverrideInfo(this.dialogRequest).subscribe(resp => {
            this.invOverride = resp;
            this.loading = false;
        }, error => {
            console.error(`Error occurred during get_override_info ${error}`);
            this.closeDialog();
        });
    }

    public ignoredChanged(isIgnored: boolean): void {
        this.invOverride.isIgnored = isIgnored;
    }

    public onApprove(): void {
        this.confirmSend().subscribe(choice => {
            if (!choice) {
                return;
            }
            this.approve(choice === 'send');
        });
    }

    private approve(sendToRars: boolean): void {
        this.loading = true;
        this.sendMessage = '';
        this.invOverride.buckets = this.invOverride.buckets.filter(b =>
            this.initBuckets.find(ib => ib.name === b.name && ib.isProtected
                && (ib.override !== null && ib.override !== b.override)));
        const approvalRequest = this.buildApprovalRequest();
        approvalRequest['sendToRars'] = sendToRars;
        if (approvalRequest['reminderDate'] !== null &&
            approvalRequest['reminderDate'] !== undefined) {
            approvalRequest['reminderDate'] = DateUtils.convertToLocaleDateString(
                approvalRequest['reminderDate'])
        }
        this.overrideSvc.approveRecommendation(approvalRequest).subscribe(resp => {
            if (!resp) {
                this.loading = false;
                return;
            }
            // the approval itself succeeded either way; only hold the modal open when the
            // send the user asked for did not happen, so they can see why
            if (this.applySendResult(resp?.rarsSend)) {
                this.closeDialog();
            }
        }, error => {
            console.error(`Error during approval: ${error}`);
        }, () => {
            this.updateComplete.emit(true);
        });
    }

    public ShowBuckets(): void {
        const value = localStorage.getItem('showHidden');
        if (value === '0') {
            localStorage.setItem('showHidden', '1');
        } else {
             localStorage.setItem('showHidden', '0');
        }
    }

    public onDeleteReminder(): void {
        const alertDialog = this.alertDialogSvc.openReminderDeleteAlertDialog(this.alertContainer);
        alertDialog.result.subscribe(dialogResult => {
            if (dialogResult instanceof DialogCloseResult || dialogResult.text === 'No') {
                return;
            }
            this.deleteReminder();
        });
    }

    public onSave(): void {
        this.checkClassConstraint().subscribe(canSave => {
            if (!canSave) {
                return;
            }
            this.confirmSend().subscribe(choice => {
                if (!choice) {
                    return;
                }
                this.saveOverride(choice === 'send');
            });
        }, error => {
            console.error(`Error during class constraint check ${error}`);
        });
    }

    /**
     * Ask whether this override should go to RARS now (RLP-4189).
     *
     * parentChildOD is non-null only when this O&D is a *child* in an OD group, in which
     * case the dialog explains why sending is not on offer rather than letting the user
     * pick it and having the server refuse.
     */
    private confirmSend(): Observable<OverrideSendChoice> {
        return this.alertDialogSvc.openSendToRarsDialog(
            this.alertContainer, this.invOverride?.parentChildOD);
    }

    /**
     * Pull the send outcome out of /inventory/override's response, which is a list of
     * [taskName, result] pairs rather than an object.
     */
    private findSendResult(resp: any): any {
        if (!Array.isArray(resp)) {
            return null;
        }
        const entry = resp.find(item => Array.isArray(item) && item[0] === 'rarsSend');
        return entry ? entry[1] : null;
    }

    /**
     * Fold the server's send outcome into the modal. Returns true when the dialog may
     * close — i.e. nothing was sent, or the send succeeded.
     */
    private applySendResult(rarsSend: any): boolean {
        if (!rarsSend) {
            return true;
        }
        // every outcome wrote a publish job row, so the bell has something new to show
        this.notificationSvc.refresh();
        if (rarsSend.status === 'Sent') {
            this.announceSent();
            return true;
        }
        // The override is saved regardless; only the send failed, so say exactly that
        // instead of leaving the user to guess which half went through.
        this.sendMessage = `The override was saved, but it was not sent to the `
            + `reservation system (${rarsSend.status}). ${rarsSend.note || ''}`.trim();
        this.loading = false;
        return false;
    }

    /** Confirm a successful send on the page, since the modal is about to close. */
    private announceSent(): void {
        try {
            this.popupSvc.displayNotification(this.notifyContainer || this.alertContainer, {
                notificationType: 'success',
                message: 'Override saved and sent to the reservation system.'
            });
        } catch (notifyError) {
            console.error(`Error showing send confirmation ${notifyError}`);
        }
    }

    private buildApprovalRequest(): any {
        return {
            origin: this.invOverride.origin,
            destination: this.invOverride.destination,
            reminderDate: this.invOverride.reminderDate,
            journeyPairs: [{
                origin: this.invOverride.origin,
                destination: this.invOverride.destination
            }],
            buckets: this.invOverride.buckets,
            rsid: this.invOverride.rsid,
            departureDate: this.invOverride.departureDate,
            effDepartureDate: this.invOverride.effDepartureDate,
            passengerClass: this.invOverride.passengerClass
        };
    }

    private buildClassConstraintRequest(): ClassContraintRequest {
        let request: ClassContraintRequest;
        let leadBucket: Bucket;

        const filteredBuckets = this.invOverride.buckets.filter((bucket) => {
            let otb = this.invOverride.intradayOtb;
            if (otb === 0) {
                otb = this.invOverride.otbTotalDemand;
            }
            if (bucket.isFlex || bucket.isProtected) {
                return false;
            } else {
                return bucket.override > otb;
            }
        });

        if (filteredBuckets.length > 0) {
            leadBucket = filteredBuckets.reduce((p, c) => p.override < c.override ? p : c);
            if (leadBucket !== undefined) {
                request = new ClassContraintRequest();
                request.rsid = this.invOverride.rsid;
                request.effDepartureDate = this.invOverride.effDepartureDate;
                request.bucketCode = leadBucket.name;
                request.journeyDestination = this.invOverride.destination;
                request.fare = leadBucket.fare;
                request.journeyOrigin = this.invOverride.origin;
                request.passengerClass = this.invOverride.passengerClass;
                request.override = leadBucket.override;
            }
        }
        return request;
    }

    private buildCommentDetails(resp: any): void {
        if (resp.comments === null || resp.comments === undefined) {
            resp.comments = {
                service: resp.rsid,
                passengerClass: resp.passengerClass,
                journeyOrigin: resp.origin,
                journeyDestination: resp.destination,
                departureDate: resp.effDepartureDate,
                comments: []
            };
        }
    }

    private buildTitle(resp: any): string {
        return ` ${resp.effDepartureDate} ${resp.rsid} (${resp.origin} - ${resp.destination}) Override`;
    }

    private buildShoulderChartRequest(resp: any): JourneyShoulderTrainRequest {
        return {
            shoulderRsid: resp.rsid,
            effDepartureDate: resp.effDepartureDate,
            passengerClass: resp.passengerClass,
            journeyOrigin: resp.origin,
            journeyDestination: resp.destination
        };
    }

    private checkClassConstraint(): any {
        return new Observable(subscriber => {
            const request = this.buildClassConstraintRequest();
            if (request !== undefined) {
                this.overrideSvc.checkClassConstraint(request).subscribe(resp => {
                    if (resp.error !== undefined && resp.error.length > 0) {
                        const alertDialog = this.alertDialogSvc.openConstraintAlertDialog(resp, this.alertContainer);
                        alertDialog.result.subscribe(dialogResult => {
                            const canContinue = !(dialogResult instanceof DialogCloseResult ||
                                dialogResult.text === 'Cancel');
                            subscriber.next(canContinue);
                        });
                    } else {
                        subscriber.next(true);
                    }
                }, error => {
                    console.error(`Error occured during class constraint API call: ${error}`);
                });
            } else {
                subscriber.next(true);
            }
        });
    }

    private deleteReminder(): void {
        const reminderRequest: ReminderRequest = {
            rsid: this.invOverride.rsid,
            passengerClass: this.invOverride.passengerClass,
            effDepartureDate: DateUtils.convertDateToStringFormat(this.invOverride.effDepartureDate),
            origin: this.invOverride.origin,
            destination: this.invOverride.destination
        };

        this.invOverride.reminderDate = null;
        this.reminderSvc.deleteReminder(reminderRequest).subscribe(() => {
        }, error => {
            console.error(`Error occurred during revert reminder`, error);
        });
    }

    private finishLoad(resp: any): void {
        this.initBuckets = JSON.parse(JSON.stringify(resp.buckets));
        this.buildCommentDetails(resp);
        this.shoulderChartRequest = this.buildShoulderChartRequest(resp);
        this.title = this.buildTitle(resp);
    }

    private getOverrideInfo(request: any): Observable<any> {
        return this.overrideSvc.getInventoryOverride(request).pipe(map(resp => {
            resp.profiles.forEach(profile => {
                let activeProfile = resp.overriddenProfile || resp.recommendedProfile
                profile.isRecommended = profile.name === activeProfile;
            });
            resp.reminderDate = resp.reminderDate ? DateUtils.convertToDate(resp.reminderDate) : null;
            this.finishLoad(resp);
            return resp;
        }));
    }

    private saveOverride(sendToRars = false): void {
        this.loading = true;
        this.sendMessage = '';
        this.invOverride.journeyPairs = [{
            origin: this.invOverride.origin,
            destination: this.invOverride.destination
        }];
        // Mutate invOverride rather than rebuilding a payload: lastUpdatedDate is the
        // optimistic-concurrency token and round-trips inside this object, so dropping it
        // would make every save 409.
        this.invOverride.sendToRars = sendToRars;
        if (this.selectedProfile) {
            this.invOverride.overrideProfile = this.selectedProfile;
        }
        if (this.invOverride['reminderDate'] !== null &&
            this.invOverride['reminderDate'] !== undefined) {
            this.invOverride['reminderDate'] = DateUtils.convertToLocaleDateString(
                this.invOverride['reminderDate'])
        }
        this.overrideSvc.saveSingleOverride(this.invOverride).subscribe(resp => {
            if (!resp) {
                this.loading = false;
                return;
            }
            if (this.applySendResult(this.findSendResult(resp))) {
                this.closeDialog();
            }
        }, error => {
            this.loading = false;
            if (error.status === 409) {
                const message = error.error?.message ||
                    'This journey\'s allocation has changed since your last refresh. ' +
                    'Please refresh to see the current allocation/override.';
                const conflictDialog = this.alertDialogSvc.openConflictDialog(message, this.alertContainer);
                conflictDialog.result.subscribe(() => {
                    this.loading = true;
                    // Refresh the grid behind this popup too, not just the popup's own
                    // data -- the grid's row for this journey is just as stale as what
                    // triggered the conflict.
                    this.updateComplete.emit(true);
                    this.getOverrideInfo(this.dialogRequest).subscribe(resp => {
                        this.invOverride = resp;
                        this.loading = false;
                    }, err => {
                        console.error(`Error occurred during get_override_info ${err}`);
                        this.loading = false;
                    });
                });
            } else {
                console.error(`Error during save ${error}`);
                this.updateComplete.emit(false);
            }
        }, () => {
            this.updateComplete.emit(true);
        });
    }

    public nestBuckets() {
        this.baseBucketOverrideGrid.buckets = this.invOverride.buckets;
        this.baseBucketOverrideGrid.overrideMetrics = this.invOverride.metrics;
        this.baseBucketOverrideGrid.nestBuckets();
        this.baseBucketOverrideGrid.scanBucketsUp();
    }
}
